'use strict';

const React = require('react');
const useState = React.useState;
const useEffect = React.useEffect;
const useRef = React.useRef;
const PropTypes = require('prop-types');
const { default: clsx } = require('clsx');
const ReactBootstrap = require('react-bootstrap');
const Media = ReactBootstrap.Media;
const { default: parse } = require('html-react-parser');
const linkifyUrls = require('linkify-urls');
const { DateTime } = require('luxon');
const { Chip, Divider, MenuItem } = require('@material-ui/core');
const { makeStyles } = require('@material-ui/core/styles');
const {
    Lock: LockIcon,
    Done: DoneIcon,
    DoneAll: DoneAllIcon,
    ErrorOutline: ErrorOutlineIcon
} = require('@material-ui/icons');
const { useInView } = require('react-intersection-observer');

const CustomContextMenu = require('../CustomContextMenu');
const UserIcon = require('../UserIcon');
const ReplyMessage = require('./ReplyMessage').default;

const { isNodeEmitter, linkify, customUrlRegexp } = require('../../utils');


const styleSheet = makeStyles((theme) => ({
    chipSmall: {
        height: 18,
        fontSize: 11
    },
    iconSmall: {
        width: 12,
        height: 12
    },
    doneIcon: {
        fontSize: 15,
        verticalAlign: 'middle',
        color: 'green'
    },
    errorOutlineIcon: {
        fontSize: 17,
        verticalAlign: 'middle',
        color: '#a94442'
    },
    item: {
        fontSize: '14px',
        fontFamily: 'inherit',
        color: '#333',
        minHeight: 0
    },
    lockIcon: {
        fontSize: 15,
        verticalAlign: 'middle',
        color: '#ccc'
    }
}));

const Message = ({
    message,
    scroll,
    cont,
    displayed,
    focus,
    removeMessage,
    editMessage,
    imdnStates,
    enableMenu,
    fromSelf,
    identity,
    reply
}) => {
    const classes = styleSheet();
    const [state, setState] = useState('');
    const [parsedContent, setParsedContent] = useState();
    const messageRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);

    const time = DateTime.fromJSDate(message.timestamp).toFormat('HH:mm');
    const { ref, inView, entry } = useInView({
        threshold: 0
    });

    const isDisplayed = React.useCallback(() => {
        if (displayed) {
            displayed();
        }
    }, [displayed]);

    useEffect(() => {
        if (parsedContent !== undefined) {
            scroll()
        }
    }, [parsedContent, scroll]
    );

    useEffect(() => {
        if (message.contentType === 'text/html') {
            setParsedContent(parse(message.content.trim(), {
                replace: (domNode) => {
                    if (domNode.attribs && domNode.attribs.href) {
                        domNode.attribs.target = '_blank';
                        return;
                    }

                    if (domNode.attribs && domNode.attribs.class) {
                        const classList = domNode.attribs.class.split(' ').filter((cls) => cls !== 'sr-only');
                        if (classList.length > 0) {
                            domNode.attribs.class = classList.join(' ');
                        } else {
                            delete domNode.attribs.class;
                        }
                    }
                    if (domNode.type === 'text') {
                        if (!domNode.parent || (domNode.parent.type === 'tag' && domNode.parent.name !== 'a')) {
                            let url = linkifyUrls(domNode.data, {
                                customUrlRegexp,
                                attributes: {
                                    target: '_blank',
                                    rel: 'noopener noreferrer'
                                }
                            });
                            return <span dangerouslySetInnerHTML={{ __html: url }} />;
                        }
                    }
                }
            }));
        } else if (message.contentType.startsWith('image/')) {
            const image = `data:${message.contentType};base64,${message.content}`
            setParsedContent(<img className="img-responsive" src={image} />);
        } else if (message.contentType === 'text/plain') {
            setParsedContent(
                <pre>{linkify(message.content)}</pre>
            );
        } else if (message.contentType === 'text/pgp-public-key') {
            setParsedContent(
                <Chip
                    component="span"
                    classes={{ sizeSmall: classes.chipSmall, iconSmall: classes.iconSmall }}
                    variant="outlined"
                    size="small"
                    icon={<LockIcon />}
                    label="Public key"
                />
            );
        }

        const finalStates = new Set(['displayed', 'received']);

        const stateChanged = (oldState, newState) => {
            setState(newState);
        };

        if (isNodeEmitter(message)
            && (message.state === 'pending' || (imdnStates && !finalStates.has(message.state)))
        ) {
            message.on('stateChanged', stateChanged);
        }
        setState(message.state);

        return () => {
            if (isNodeEmitter(message)) {
                message.removeListener('stateChanged', stateChanged);
            }
        }
    }, [message, classes]) // eslint-disable-line react-hooks/exhaustive-deps

    const scrollToMessage = () => {
        messageRef.current.scrollIntoView({ behavior: 'smooth' })
    };

    useEffect(() => {
        if (messageRef.current !== null && focus === true) {
            scrollToMessage()
        }
    }, [focus]);

    useEffect(() => {
        if (inView) {
            isDisplayed();
        }
    }, [inView, isDisplayed]);

    let theme = clsx({
        'text-left': true,
        'pending': state === 'pending',
        'text-danger': state === 'failed' || state === 'error',
        'continued': cont && message.type !== 'status',
        'status': message.type === 'status'
    });

    const statusIcon = () => {
        if (state === 'accepted') {
            return (<DoneIcon style={{ color: '#888' }} className={classes.doneIcon} />);
        }
        if (state === 'delivered') {
            return (<DoneIcon className={classes.doneIcon} />);
        }
        if (state === 'displayed') {
            return (<DoneAllIcon className={classes.doneIcon} />);
        }
        if (state === 'failed') {
            return (<ErrorOutlineIcon className={classes.errorOutlineIcon} titleAccess="Not Delivered" />);
        }
        if (state === 'error') {
            return (<ErrorOutlineIcon className={classes.errorOutlineIcon} titleAccess="Display Error" />);
        }
    };

    const handleContextMenu = (e) => {
        if (!enableMenu) {
            return
        }

        e.preventDefault();
        const { clientX, clientY } = e;
        const virtualElement = {
            clientWidth: 0,
            clientHeight: 0,
            getBoundingClientRect: () => ({
                width: 0,
                height: 0,
                top: clientY,
                right: clientX,
                bottom: clientY,
                left: clientX
            })
        };
        setAnchorEl(virtualElement);
    }

    const copy = () => {
        let selection = window.getSelection();
        if (selection.toString() === '') {
            selection = message.content;
        }
        if ('clipboard' in navigator) {
            navigator.clipboard.writeText(selection);
        } else {
            document.execCommand('copy', true, selection);
        }
    };

    const _removeMessage = () => {
        if (typeof removeMessage === 'function') {
            removeMessage();
        }
    }

    const _editMessage = () => {
        if (typeof editMessage === 'function') {
            editMessage();
        }
    }

    const handleClose = () => {
        setAnchorEl(null);
    };

    if (cont || message.type === 'status') {
        return (
            <div ref={ref}>
                <Media className={theme} onContextMenu={handleContextMenu}>
                    {enableMenu && message.type !== 'status' &&
                        <CustomContextMenu
                            open={Boolean(anchorEl)}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            keepMounted={false}
                        >
                            <MenuItem className={classes.item} onClick={() => { copy(); handleClose() }}>
                                Copy
                            </MenuItem>
                            <Divider />
                            {fromSelf &&
                                <MenuItem className={classes.item} onClick={() => { _editMessage(); handleClose() }}>
                                    Edit Message
                                </MenuItem>
                            }
                            <MenuItem className={classes.item} onClick={() => { _removeMessage(); handleClose() }}>
                                Remove Message
                            </MenuItem>
                        </CustomContextMenu>
                    }

                    <div ref={messageRef} />
                    {message.type !== 'status' &&
                        <Media.Left className="timestamp-continued"><span>{time}</span></Media.Left>
                    }
                    <Media.Body className="vertical-center">
                        {reply?.snapshot &&
                            <ReplyMessage message={reply.snapshot} continues={true} />
                        }
                        {parsedContent}
                    </Media.Body>
                    <Media.Right>
                        <span className="pull-right" style={{ paddingRight: '15px', whiteSpace: 'nowrap' }}>
                            {message.isSecure && <LockIcon className={classes.lockIcon} />}
                            {statusIcon()}
                            {message.type === 'status' &&
                                <pre>{time}</pre>
                            }
                        </span>
                    </Media.Right>
                </Media>
            </div >
        );
    }

    return (
        <div ref={ref}>
            <Media className={theme} onContextMenu={handleContextMenu}>
                {enableMenu &&
                    <CustomContextMenu
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        keepMounted={false}
                    >
                        <MenuItem className={classes.item} onClick={() => { copy(); handleClose() }}>
                            Copy
                        </MenuItem>
                        <Divider />
                        {fromSelf &&
                            <MenuItem className={classes.item} onClick={() => { _editMessage(); handleClose() }}>
                                Edit Message
                            </MenuItem>
                        }
                        <MenuItem className={classes.item} onClick={() => { _removeMessage(); handleClose() }}>
                            Remove Message
                        </MenuItem>
                    </CustomContextMenu>
                }
                <div ref={messageRef} />
                <Media.Left>
                    <UserIcon identity={identity} />
                </Media.Left>
                <Media.Body className="vertical-center">
                    <Media.Heading>
                        {identity.displayName || identity.uri}&nbsp;
                        <span>{time}</span>
                        <span className="pull-right" style={{ paddingRight: '15px' }}>
                            {message.isSecure && <LockIcon className={classes.lockIcon} />}
                            {statusIcon()}
                        </span>
                    </Media.Heading>
                    <div style={{ paddingRight: '15px' }}>
                        {reply?.snapshot &&
                            <ReplyMessage message={reply.snapshot} />
                        }
                        {parsedContent}
                    </div>
                </Media.Body>
            </Media>
        </div>
    );
};

Message.propTypes = {
    message: PropTypes.object.isRequired,
    scroll: PropTypes.func.isRequired,
    removeMessage: PropTypes.func,
    cont: PropTypes.bool,
    displayed: PropTypes.func,
    focus: PropTypes.bool,
    imdnStates: PropTypes.bool,
    enableMenu: PropTypes.bool,
    editMessage: PropTypes.func,
    fromSelf: PropTypes.bool,
    identity: PropTypes.object.isRequired,
    reply: PropTypes.object
};


module.exports = Message;
